import React, { FC, useEffect, useRef, useState } from 'react';
import find from 'lodash/find';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import { Redirect, Route } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import TableList, { TabConfig } from './TableList';
import useResetErrorsOnUnmount from 'src/presentation/hooks/useResetErrorsOnUnmount';
import useRouter from 'src/presentation/hooks/useRouter';
import { FullPageModal, FullPageSpinner, CloseButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { InitialSorting } from './Table';

export type TablePageRender<T = unknown> =
  | FC<T>
  | {
      ({ root }: { root: string }): JSX.Element | null;
    };

type TablePageProps = {
  'data-aid': string;
  actions?: any;
  backButtonUrl?: string;
  backButtonLabel?: string;
  className?: string;
  columnConfig?: any;
  context?: any;
  description?: any;
  disableSearch?: boolean;
  dragEndHandler?: Function;
  editor?: any;
  emptyFilterImage?: any;
  emptyFilterState?: any;
  emptyState?: any;
  emptyStateFullHeightLayout?: boolean;
  emptyStateHideTableHeader?: boolean;
  emptyStateRender?: any;
  errors: any;
  filterFunc?: Function;
  filterPlaceholder?: string;
  initialSearchString?: string;
  initialSorting?: InitialSorting;
  isLoading: boolean;
  items: any;
  itemsName?: string;
  modalCloseButtonLabel?: string;
  modalCloseButtonType?: CloseButtonTypes;
  modalTopPadding?: number;
  name: string;
  onTabChange?: Function;
  pagination?: { pageSize: number; itemsTitle: string };
  preselectedTab?: string;
  preventCloseOnEscape?: boolean;
  renderInPortal?: boolean;
  renderRow?: Function;
  renderRowActions?: Function;
  resetErrors?: any;
  root: string;
  skipItemExistenceCheck?: boolean;
  leftBottomHeaderSlot?: JSX.Element;
  tabConfig?: TabConfig[];
  title?: string;
  updateUrlOnTabChange?: boolean;
};

const TablePage: FC<TablePageProps> = ({
  'data-aid': dataAid,
  actions,
  backButtonUrl,
  backButtonLabel,
  className,
  columnConfig,
  context,
  description,
  disableSearch,
  dragEndHandler,
  editor,
  emptyFilterImage,
  emptyFilterState,
  emptyState,
  emptyStateFullHeightLayout,
  emptyStateHideTableHeader,
  emptyStateRender,
  errors,
  filterFunc,
  filterPlaceholder = 'Search',
  initialSearchString,
  initialSorting,
  isLoading,
  items,
  itemsName,
  modalCloseButtonLabel,
  modalCloseButtonType,
  modalTopPadding,
  name,
  onTabChange,
  pagination,
  preselectedTab,
  preventCloseOnEscape,
  renderRow,
  renderRowActions,
  resetErrors,
  root,
  skipItemExistenceCheck,
  leftBottomHeaderSlot,
  tabConfig,
  updateUrlOnTabChange,
  renderInPortal,
}) => {
  const { match } = useRouter();
  const EmptyState = emptyState;
  const getEditorRoute = (config, items, errors, resetErrors, dataAid) => {
    const editorRoute = config.route || '';
    return (
      <Route
        exact
        path={`${match.path}${editorRoute ? `/${editorRoute}` : ''}/:id`}
        key={editorRoute}
        render={({ match }) => {
          return (
            <CachedModal
              closeButtonLabel={config.closeButtonLabel}
              closeButtonType={config.closeButtonType}
              context={config.context}
              dataAid={dataAid}
              editor={config.editor}
              errors={errors}
              isLoading={isLoading}
              items={items}
              match={match}
              preventCloseOnEscape={preventCloseOnEscape}
              resetErrors={resetErrors}
              renderInPortal={renderInPortal}
              root={root}
              topPadding={config.topPadding}
              skipItemExistenceCheck={skipItemExistenceCheck}
            />
          );
        }}
      />
    );
  };

  const renderEditorRoutes = () => {
    if (Array.isArray(editor)) {
      return editor.map(config => {
        const itemsKey = config.itemsKey;

        return getEditorRoute(config, items[itemsKey], errors[itemsKey], resetErrors[itemsKey], dataAid);
      });
    }

    const config = {
      editor: editor,
      context: context,
      closeButtonType: modalCloseButtonType,
      closeButtonLabel: modalCloseButtonLabel,
      topPadding: modalTopPadding,
    };

    return getEditorRoute(config, items, errors, resetErrors, dataAid);
  };

  return (
    <>
      <Route
        path={match.path}
        render={() =>
          (!items || !items.length) && !isLoading && EmptyState ? (
            <EmptyState actions={actions} />
          ) : (
            <TableList
              actions={actions}
              backButtonLabel={backButtonLabel}
              backButtonUrl={backButtonUrl}
              className={className}
              columnConfig={columnConfig}
              dataAid={dataAid}
              description={description}
              disableSearch={disableSearch}
              dragEndHandler={dragEndHandler}
              emptyFilterImage={emptyFilterImage}
              emptyFilterState={emptyFilterState}
              emptyStateFullHeightLayout={emptyStateFullHeightLayout}
              emptyStateHideTableHeader={emptyStateHideTableHeader}
              emptyStateRender={emptyStateRender}
              filterFunc={filterFunc}
              filterPlaceholder={filterPlaceholder}
              initialSearchString={initialSearchString}
              initialSorting={initialSorting}
              isLoading={isLoading}
              items={items}
              leftBottomHeaderSlot={leftBottomHeaderSlot}
              onTabChange={onTabChange}
              pageName={itemsName || name}
              pagination={pagination}
              preselectedTab={preselectedTab}
              renderRow={renderRow}
              renderRowActions={renderRowActions}
              root={root}
              tabConfig={tabConfig}
              updateUrlOnTabChange={updateUrlOnTabChange}
            />
          )
        }
      />
      {renderEditorRoutes()}
    </>
  );
};

type CachedModalProps = {
  closeButtonLabel?: string;
  closeButtonType?: any;
  context?: any;
  dataAid: string;
  editor: any;
  errors: any;
  isLoading: boolean;
  items: any;
  match: any;
  preventCloseOnEscape?: boolean;
  resetErrors: any;
  renderInPortal?: boolean;
  root: any;
  skipItemExistenceCheck?: boolean;
  topPadding?: any;
};

const CachedModal: FC<CachedModalProps> = ({
  closeButtonLabel,
  closeButtonType,
  context,
  dataAid,
  editor,
  errors,
  isLoading,
  items,
  match,
  preventCloseOnEscape,
  resetErrors,
  renderInPortal,
  root,
  skipItemExistenceCheck,
  topPadding,
}) => {
  const { history } = useRouter();
  const dispatch = useDispatch();
  const actionsRef = useRef();

  const onClose = () => {
    const editorOnCloseHandler: any = get(actionsRef, 'current.onCloseHandler');
    if (isFunction(editorOnCloseHandler)) {
      editorOnCloseHandler();
      return;
    }

    dispatch(resetErrors());
    history.push({ pathname: root, search: history.location.search });
  };

  const footerActions = <></>;

  const cachedMatch = useCachedMatch(match);
  const content = isLoading ? (
    <FullPageSpinner />
  ) : (
    getContent(resetErrors, editor, cachedMatch, items, root, errors, actionsRef, context, skipItemExistenceCheck)
  );

  return (
    <FullPageModal
      closeButtonType={closeButtonType}
      closeButtonLabel={closeButtonLabel}
      content={content}
      data-aid={`${dataAid}-modal-editor`}
      footerActions={footerActions}
      onClose={onClose}
      visible={match != null}
      topPadding={topPadding}
      preventCloseOnEscape={preventCloseOnEscape}
      renderInPortal={renderInPortal}
    />
  );
};

function useCachedMatch(match) {
  const [matchState, setMatchState] = useState(match);
  const matchRef = useRef(matchState);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!match && matchRef.current) {
      timeoutRef.current = window.setTimeout(() => {
        setMatchState(match);
      }, FullPageModal.exitDuration);
    } else {
      setMatchState(match);
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, [match]);

  return matchState;
}

function getContent(
  resetErrors: any,
  editor: any,
  match: any,
  items: any,
  root: string,
  errors: any,
  actionsRef: any,
  context: any,
  skipItemExistenceCheck?: boolean
) {
  if (!match) {
    return null;
  }

  return (
    <EditorDisplayer
      skipItemExistenceCheck={skipItemExistenceCheck}
      errors={errors}
      editor={editor}
      items={items}
      match={match}
      actionsRef={actionsRef}
      resetErrors={resetErrors}
      root={root}
      context={context}
    />
  );
}

const EditorDisplayer = (props: any) => {
  const { actionsRef, skipItemExistenceCheck, editor, errors, items, match, resetErrors, root, context } = props;

  useResetErrorsOnUnmount(resetErrors);

  const Editor = editor;

  const isEdit = match.params.id !== 'new';
  const item = find(items, item => String(item.id) === match.params.id);
  const noItemToEdit = isEdit && !item;

  // skipItemExistenceCheck - allows open Editor page even if the item is not exist in the table
  if (!skipItemExistenceCheck && noItemToEdit) {
    return <Redirect to={`${root}`} />;
  }

  return <Editor actionsRef={actionsRef} errors={errors} item={item} root={root} context={context} />;
};

export default TablePage;
